import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';

/**
 * The two smaller external records, in one page: a purchase *attempt* and an
 * *activity*.
 *
 * Both are read-only lists of fields from the venue's system, both are reached
 * from somewhere else (a signup's timeline, an external purchase, a seasonal
 * slot), and neither has an action. Two files would be two copies of the same
 * five lines; what differs is which fields to show, which is a table.
 *
 * The attempt's `result` is the same numeric enum the external purchase uses, and
 * an unmapped number is shown as itself rather than as "Unknown".
 */

const ATTEMPT_RESULT = {
  0: 'Undefined',
  1: 'Pending',
  2: 'Failed',
  4: 'Success',
  5: 'Cancellation success',
  6: 'Cancellation failed'
};

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const minutes = value => {
  if (value == null) return null;
  // Sometimes a number of minutes, sometimes an ISO duration.
  if (typeof value === 'number') return `${value} min`;
  return String(value);
};

const KINDS = {
  attempt: {
    title: 'External purchase attempt',
    back: { label: '← Signups', to: '/next/signups' },
    load: (api, id) => api.externalPurchaseAttempt.getExternalPurchaseAttemptById(id),
    heading: data => (data.result != null
      ? (ATTEMPT_RESULT[data.result] || `Result ${data.result}`)
      : 'Attempt'),
    pill: data => (data.result != null
      ? (ATTEMPT_RESULT[data.result] || String(data.result))
      : null),
    facts: data => [
      ['Signup id', data.signupId
        ? <Guid value={data.signupId} to={`/next/signup/${data.signupId}`} />
        : <span className="next-muted">—</span>],
      ['External purchase id', data.externalPurchaseId
        ? <Guid value={data.externalPurchaseId} to={`/next/external-purchase/${data.externalPurchaseId}`} />
        : <span className="next-muted">—</span>],
      ['Started at', fmtDate(data.startedAt)],
      ['Ended at', fmtDate(data.endedAt)],
      ['Error text', data.errorText || <span className="next-muted">—</span>]
    ],
    links: data => [
      data.signupId && { label: 'Signup', to: `/next/signup/${data.signupId}` },
      data.externalPurchaseId && {
        label: 'External purchase',
        hint: 'and its other attempts',
        to: `/next/external-purchase/${data.externalPurchaseId}`
      }
    ]
  },

  activity: {
    title: 'External activity',
    back: { label: '← Activities', to: '/next/activities' },
    load: (api, id) => api.externalActivities.getExternalActivity(id),
    heading: data => data.title || 'External activity',
    pill: data => (data.isBuried ? 'Buried' : null),
    facts: data => [
      ['Starts at', fmtDate(data.startsAt)],
      ['Ends at', fmtDate(data.endsAt)],
      ['Duration', minutes(data.duration) || <span className="next-muted">—</span>],
      ['Price', data.price != null ? `${data.price} DKK` : <span className="next-muted">—</span>],
      ['Available spots', data.availableSpots ?? <span className="next-muted">—</span>],
      ['Coach', data.coach || <span className="next-muted">—</span>],
      ['Integration id', data.integrationId
        ? <Guid value={data.integrationId} href={`/integrations?id=${data.integrationId}`} />
        : <span className="next-muted">—</span>],
      ['Crawling id', data.crawlingId
        ? <Guid value={data.crawlingId} to={`/next/crawling/${data.crawlingId}`} />
        : <span className="next-muted">—</span>],
      ['Integrated resource id', data.integratedResourceId || <span className="next-muted">—</span>],
      ['External reference', data.externalReference || <span className="next-muted">—</span>],
      ['Buried', data.isBuried ? 'Yes' : 'No'],
      ['Created at', fmtDate(data.createdAt)],
      // The hash is how a crawled activity is recognised again on the next run;
      // its ingredients are what went into it, and they are the answer when the
      // same slot keeps being re-created.
      ['Hash', data.hash ? <span className="next-mono-list">{data.hash}</span> : <span className="next-muted">—</span>],
      ['Hash ingredients', data.hashIngredients
        ? <span className="next-mono-list">{data.hashIngredients}</span>
        : <span className="next-muted">—</span>]
    ],
    links: data => [
      data.crawlingId && { label: 'Crawling', hint: 'the run that produced it', to: `/next/crawling/${data.crawlingId}` },
      data.crawlingId && { label: 'Activities from that run', to: `/next/activities?crawlingId=${data.crawlingId}` },
      data.integrationId && { label: 'Integration', href: `/integrations?id=${data.integrationId}` }
    ]
  }
};

const ExternalRecordInternal = ({ api, kind }) => {
  const params = useParams();
  const id = params.recordId;
  const spec = KINDS[kind];

  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const answer = await spec.load(api, id);
      const payload = answer?.data || answer;
      if (!payload || !payload.id) {
        setFailed(true);
        return;
      }
      setData(payload);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, id, spec]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to={spec.back.to}>{spec.back.label}</BackToList>
        <div className="next-note next-note--error">
          Could not load this record.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!data) return <div className="next-page next-muted">Loading…</div>;

  const pill = spec.pill(data);

  return (
    <div className="next-page next-page--wide">
      <BackToList to={spec.back.to}>{spec.back.label}</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{spec.heading(data)}</h1>
          {pill && <StatusPill status={pill}>{pill}</StatusPill>}
        </div>
        <span className="next-detail__id">{data.id}</span>
      </header>

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>{spec.title}</h2></div>
            <div className="next-facts">
              {spec.facts(data).map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks items={spec.links(data)} />
          </section>
        </aside>
      </div>
    </div>
  );
};

ExternalRecordInternal.propTypes = {
  api: PropTypes.object.isRequired,
  kind: PropTypes.oneOf(['attempt', 'activity']).isRequired
};

const ExternalRecord = withApi(ExternalRecordInternal);

ExternalRecord.requiresLogin = true;

export default ExternalRecord;
